import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { lessonFeedback } from '@platform/client';
import { useQuery } from '@tanstack/react-query';

import { helpfulShown, reasonsShown, shownDate } from '../library/feedback';

/**
 * What learners said of this lesson — totals per published version, so an edit can be
 * judged against the version before it, and the answers whose learners have written
 * to support, which is the only case the console may put a name to one.
 */
export function LearnerFeedback({ lessonId }: { lessonId: string }) {
  const report = useQuery({
    queryKey: ['lesson-feedback', lessonId],
    queryFn: () =>
      lessonFeedback({ path: { id: lessonId }, throwOnError: true }),
  });

  const data = report.data?.data;

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="learner-feedback">
      <Stack spacing={1.5}>
        <Typography variant="subtitle2">
          Learner feedback
          {data === undefined ? '' : ` — live version v${String(data.version)}`}
        </Typography>

        {report.isPending && <LinearProgress />}
        {report.isError && (
          <Alert severity="error">Could not load the feedback.</Alert>
        )}

        {data?.versions.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No learner has answered yet.
          </Typography>
        )}

        {data !== undefined && data.versions.length > 0 && (
          <Table size="small" aria-label="Feedback by version">
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Helpful</TableCell>
                <TableCell>Yes / Not really</TableCell>
                <TableCell>Answers</TableCell>
                <TableCell>Reasons</TableCell>
                <TableCell>Last feedback</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.versions.map((version) => (
                <TableRow key={version.version}>
                  <TableCell>{`v${String(version.version)}`}</TableCell>
                  <TableCell>{helpfulShown(version)}</TableCell>
                  <TableCell>
                    {`${String(version.helpful)} / ${String(version.notHelpful)}`}
                  </TableCell>
                  <TableCell>{version.count}</TableCell>
                  <TableCell>{reasonsShown(version.reasons)}</TableCell>
                  <TableCell>{shownDate(version.lastAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {data !== undefined && data.named.length > 0 && (
          <>
            <Typography variant="subtitle2">
              From learners who have contacted support
            </Typography>
            <Table size="small" aria-label="Named answers">
              <TableHead>
                <TableRow>
                  <TableCell>Learner</TableCell>
                  <TableCell>Answer</TableCell>
                  <TableCell>Reasons</TableCell>
                  <TableCell>Score</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>When</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.named.map((one) => (
                  <TableRow key={`${one.email}-${one.answeredAt}`}>
                    <TableCell>{`${one.name} <${one.email}>`}</TableCell>
                    <TableCell>{one.helpful ? 'Yes' : 'Not really'}</TableCell>
                    <TableCell>{reasonsShown(one.reasons)}</TableCell>
                    <TableCell>{`${String(one.scorePct)}%`}</TableCell>
                    <TableCell>{`v${String(one.lessonVersion)}`}</TableCell>
                    <TableCell>{shownDate(one.answeredAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </Stack>
    </Paper>
  );
}
